import {
  PaginationState,
  Row,
  SortingState,
  Table,
} from '@tanstack/react-table';
import { FC, PropsWithChildren, ReactElement } from 'react';

import { MessageType } from '@savgroup-front-common/types';

import { Skeleton } from '../../../atoms/Skeleton';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import { useIsNewUiEnabled } from '../../../hooks/useIsNewUiEnabled';
import { FloatingLoader } from '../../../molecules/FloatingLoader';

import Body from './Body';
import { useGetDatatableCoreHeight } from './DatatableCore.hooks';
import {
  $NoResultContainer,
  $StyledTable,
  $StyledTBody,
  $TableContainer,
} from './DatatableCore.styles';
import Header from './Header';

interface TableProps<D> {
  table: Table<D>;
  sorting?: SortingState;
  isLoading: boolean;
  pagination: PaginationState;
  SubComponent?: FC<{ row: Row<D> }>;
  portalId?: string;
  isSubRowStyled?: boolean;
  isExpandedBackgroundEnabled?: boolean;
  isPaginationEnabled: boolean;
  onClickRow?: (row: any) => void;
  withHeader: boolean;
  withMinimalWidth: boolean;
  heightOffset: number;
  withMarginBottom?: boolean;
  hasScrollBody?: boolean;
  noResultText?: MessageType;
  dataTestId?: string;
}

const DatatableCore = <D = unknown,>({
  table,
  sorting,
  isLoading,
  pagination,
  SubComponent,
  portalId,
  isSubRowStyled,
  isExpandedBackgroundEnabled,
  isPaginationEnabled,
  onClickRow,
  withHeader,
  withMinimalWidth,
  withMarginBottom = true,
  hasScrollBody = false,
  heightOffset,
  noResultText,
  dataTestId,
}: PropsWithChildren<TableProps<D>>): ReactElement => {
  const isNewUiEnabled = useIsNewUiEnabled();
  const height = useGetDatatableCoreHeight({
    isPaginationEnabled,
    heightOffset,
  });
  const { rows } = table.getRowModel();

  return (
    <$TableContainer
      $withMarginBottom={withMarginBottom}
      $height={hasScrollBody ? height : undefined}
    >
      {portalId && <div id={portalId} />}
      <$StyledTable
        $withMinimalWidth={withMinimalWidth}
        $isNewUiEnabled={isNewUiEnabled}
      >
        {withHeader && (!isLoading || rows.length > 0) && (
          <Header<D> table={table} sorting={sorting} dataTestId={dataTestId} />
        )}
        <$StyledTBody $isNewUiEnabled={isNewUiEnabled}>
          {(!isLoading || rows.length > 0) && (
            <Body<D>
              table={table}
              SubComponent={SubComponent}
              isSubRowStyled={isSubRowStyled}
              isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
              onClickRow={onClickRow}
            />
          )}
        </$StyledTBody>
      </$StyledTable>
      {isLoading && rows.length === 0 && (
        <>
          {new Array(pagination.pageSize).fill(null).map((_x, index) => (
            <Skeleton key={index} height="2rem" marginBottom="0.5rem" />
          ))}
        </>
      )}
      {!isLoading && rows.length === 0 && noResultText && (
        <$NoResultContainer>
          <SafeFormattedMessageWithoutSpread message={noResultText} />
        </$NoResultContainer>
      )}
      {isLoading && rows.length > 0 && <FloatingLoader />}
    </$TableContainer>
  );
};

export default DatatableCore;
